import { useState, useRef, useEffect, useCallback } from 'react';
import { Tooltip } from './Tooltip';
import { cn } from '@/lib/utils';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface TruncatedTextProps {
  /** The text to display (used both for rendering and tooltip) */
  text: string;
  /** Number of lines before truncation. 1 = single-line truncate, 2+ = line-clamp-N */
  lines?: number;
  /** Additional classes for the text element */
  className?: string;
  /** HTML element to render as */
  as?: 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'div';
  /** Tooltip position */
  tooltipPosition?: TooltipPosition;
  /** Max width of the tooltip */
  tooltipMaxWidth?: number;
}

export function TruncatedText({
  text,
  lines = 1,
  className,
  as: Tag = 'p',
  tooltipPosition = 'top',
  tooltipMaxWidth = 400,
}: TruncatedTextProps) {
  const textRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = textRef.current;
    if (!el) return;

    if (lines === 1) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    } else {
      setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    }
  }, [lines]);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    checkTruncation();

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkTruncation, text]);

  const truncationClass = lines === 1 ? 'truncate' : `line-clamp-${lines}`;

  const element = (
    <Tag
      ref={textRef as React.Ref<never>}
      className={cn(truncationClass, isTruncated && 'cursor-help', className)}
    >
      {text}
    </Tag>
  );

  if (!isTruncated) return element;

  return (
    <Tooltip
      content={<span className="whitespace-pre-wrap break-words">{text}</span>}
      position={tooltipPosition}
      maxWidth={tooltipMaxWidth}
    >
      {element}
    </Tooltip>
  );
}
